
import { useState } from 'react';

interface CompanyListProps {
  onCreateCompany: () => void;
}

const CompanyList = ({ onCreateCompany }: CompanyListProps) => {
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [showCompanyDetails, setShowCompanyDetails] = useState(false);

  const companies = [
    {
      id: 1,
      name: 'TechCorp Solutions',
      domain: 'techcorp.teamsday.com',
      logo: 'https://public.readdy.ai/ai/img_res/96d9e23024dca8f0bcbfe0a6e0e264ae.jpg',
      status: 'active',
      users: 45,
      plan: 'Enterprise',
      created: '2024-01-15',
      lastActive: '2 hours ago',
      customization: {
        primaryColor: '#2563eb',
        secondaryColor: '#64748b',
        logoUrl: 'custom-logo.png'
      }
    },
    {
      id: 2,
      name: 'BuildPro Construction',
      domain: 'buildpro.teamsday.com',
      logo: 'https://readdy.ai/api/search-image?query=construction%20company%20logo%20with%20orange%20and%20black%20colors%2C%20building%20tools%20and%20hard%20hat%2C%20professional%20industrial%20design&width=64&height=64&seq=2&orientation=squarish',
      status: 'active',
      users: 28,
      plan: 'Professional',
      created: '2024-02-03',
      lastActive: '1 day ago',
      customization: {
        primaryColor: '#ea580c',
        secondaryColor: '#374151',
        logoUrl: 'buildpro-logo.png'
      }
    },
    {
      id: 3,
      name: 'DesignStudio Pro',
      domain: 'designstudio.teamsday.com',
      logo: 'https://readdy.ai/api/search-image?query=creative%20design%20studio%20logo%20with%20purple%20and%20pink%20gradient%2C%20artistic%20brush%20strokes%2C%20modern%20creative%20agency%20branding&width=64&height=64&seq=3&orientation=squarish',
      status: 'trial',
      users: 12,
      plan: 'Trial',
      created: '2024-03-10',
      lastActive: '3 hours ago',
      customization: {
        primaryColor: '#7c3aed',
        secondaryColor: '#ec4899',
        logoUrl: 'design-logo.png'
      }
    },
    {
      id: 4,
      name: 'GreenEnergy Corp',
      domain: 'greenenergy.teamsday.com',
      logo: 'https://readdy.ai/api/search-image?query=renewable%20energy%20company%20logo%20with%20green%20and%20blue%20colors%2C%20solar%20panels%20and%20wind%20turbines%2C%20sustainable%20technology%20branding&width=64&height=64&seq=4&orientation=squarish',
      status: 'suspended',
      users: 0,
      plan: 'Basic',
      created: '2024-01-28',
      lastActive: '2 weeks ago',
      customization: {
        primaryColor: '#059669',
        secondaryColor: '#0ea5e9',
        logoUrl: 'green-logo.png'
      }
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'trial': return 'bg-yellow-100 text-yellow-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCompanyClick = (companyId: number) => {
    setSelectedCompany(companyId);
    setShowCompanyDetails(true);
  };

  const selectedCompanyData = companies.find(c => c.id === selectedCompany);

  return (
    <>
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Company Platforms</h2>
            <p className="text-gray-600">Manage all company instances and their configurations</p>
          </div>
          <button
            onClick={onCreateCompany}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium whitespace-nowrap"
          >
            <i className="ri-add-line mr-2"></i>
            Create Company
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-building-line text-blue-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Companies</p>
              <p className="text-2xl font-bold text-gray-900">4</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-user-line text-green-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">85</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <i className="ri-time-line text-yellow-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Trial Accounts</p>
              <p className="text-2xl font-bold text-gray-900">1</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-red-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Monthly Revenue</p>
              <p className="text-2xl font-bold text-gray-900">$12,450</p>
            </div>
          </div>
        </div>
      </div>

      {/* Companies Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">All Companies</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Users</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Active</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {companies.map((company) => (
                <tr key={company.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <img
                        src={company.logo}
                        alt={company.name}
                        className="w-10 h-10 rounded-lg object-cover mr-3"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{company.name}</div>
                        <div className="text-sm text-gray-500">Created {company.created}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{company.domain}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(company.status)}`}>
                      {company.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {company.users}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {company.plan}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {company.lastActive}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleCompanyClick(company.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <i className="ri-eye-line"></i>
                      </button>
                      <button className="text-blue-600 hover:text-blue-900">
                        <i className="ri-edit-line"></i>
                      </button>
                      <button className="text-gray-600 hover:text-gray-900">
                        <i className="ri-settings-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Company Details Modal */}
      {showCompanyDetails && selectedCompanyData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Company Details</h3>
              <button
                onClick={() => setShowCompanyDetails(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Company Info */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Company Information</h4>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <img
                        src={selectedCompanyData.logo}
                        alt={selectedCompanyData.name}
                        className="w-16 h-16 rounded-lg object-cover mr-4"
                      />
                      <div>
                        <h5 className="text-xl font-semibold text-gray-900">{selectedCompanyData.name}</h5>
                        <p className="text-gray-600">{selectedCompanyData.domain}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedCompanyData.status)}`}>
                          {selectedCompanyData.status}
                        </span>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Plan</label>
                        <p className="text-sm text-gray-900">{selectedCompanyData.plan}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Users</label>
                        <p className="text-sm text-gray-900">{selectedCompanyData.users}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Created</label>
                        <p className="text-sm text-gray-900">{selectedCompanyData.created}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Customization Preview */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Branding Preview</h4>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div 
                      className="h-32 rounded-lg mb-4 flex items-center justify-center"
                      style={{ backgroundColor: selectedCompanyData.customization.primaryColor }}
                    >
                      <div className="text-white font-bold text-lg">{selectedCompanyData.name}</div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Primary Color</span>
                        <div 
                          className="w-6 h-6 rounded border"
                          style={{ backgroundColor: selectedCompanyData.customization.primaryColor }}
                        ></div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Secondary Color</span>
                        <div 
                          className="w-6 h-6 rounded border"
                          style={{ backgroundColor: selectedCompanyData.customization.secondaryColor }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 whitespace-nowrap">
                  <i className="ri-download-line mr-2"></i>
                  Export Data
                </button>
                <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 whitespace-nowrap">
                  <i className="ri-edit-line mr-2"></i>
                  Edit Company
                </button>
                <button className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 whitespace-nowrap">
                  <i className="ri-settings-line mr-2"></i>
                  Manage Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CompanyList;
